import { Injectable } from '@angular/core';
import { LoginFor, User } from '@app/shared/models/User';
import { Http } from '@capacitor-community/http';
import { Capacitor, HttpResponse } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { AccountsService } from '@core/services/accounts/accounts.service';
import { environment } from '@env';
import { NavController } from '@ionic/angular';
import { GmailMessageResponse } from '@shared/models/GmailMessageResponse';
import { DrivePickerService } from '../drive-picker/drive-picker.service';
import { LoadingService } from '../loading/loading.service';
import { StorageService } from '../storage/storage.service';
import { ToastService } from '../toast/toast.service';
import { AlertService } from '../alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ADD_FILE_OPTION_NAME } from '@app/core/constants/app.enum';
import { GenericOAuth2, OAuth2AuthenticateOptions } from '@capacitor-community/generic-oauth2';

@Injectable({
  providedIn: 'root',
})
export class GoogleService {
  ttl: number = 60 * 1 * 30;

  private gmailTotalResults = 0;

  private OAuth2AuthenticateOptions: OAuth2AuthenticateOptions = {
    authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/auth',
    accessTokenEndpoint: 'https://www.googleapis.com/oauth2/v4/token',
    scope: 'email profile https://www.googleapis.com/auth/drive.file',
    resourceUrl: 'https://www.googleapis.com/userinfo/v2/me',
    logsEnabled: true,
    web: {
      appId: environment.GAPI_CLIENT_ID,
      responseType: 'token',
      accessTokenEndpoint: '',
      redirectUrl: 'http://localhost:8100',
      windowOptions: '',
    },
    android: {
      appId: '976302890078-l1qoa32m744qartd03hpgcdfqlte928s.apps.googleusercontent.com',
      responseType: 'code',
      redirectUrl: 'com.eprintitsaas.mobile:/',
    },
    ios: {
      appId: environment.GAPI_CLIENT_ID_IOS,
      responseType: 'code',
      redirectUrl: 'com.googleusercontent.apps.976302890078-g97ora02oc2ej22cdc4k51g7fgab9kbm:/',
    },
  };

  isGoogleSignIn = false;

  constructor(
    private storageSvc: StorageService,
    private accounts: AccountsService,
    // private iab: InAppBrowser,
    private navCtrl: NavController,
    private toastSvc: ToastService,
    private loadingSvc: LoadingService,
    private drivePickerService: DrivePickerService,
    private accountsSvc: AccountsService,
    private translateService: TranslateService,
    private alertService: AlertService
  ) {}

  initGoogleLogin() {
    let scopes = [
      'openid',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/userinfo.profile',
      // "https://www.googleapis.com/auth/gmail.readonly",
      'https://www.googleapis.com/auth/drive.file',
    ];
    if (Capacitor.getPlatform() === 'ios') {
      GoogleAuth.initialize({
        scopes,
        grantOfflineAccess: true,
      });
    } else {
      GoogleAuth.initialize({
        clientId: environment.GAPI_CLIENT_ID,
        scopes,
        grantOfflineAccess: true,
      });
    }
    this.drivePickerService.pickerInit();
  }

  // NEW START
  async signIn(type: string) {
    if(this.isGoogleSignIn){
      return;
    }
    this.isGoogleSignIn = true;
    // let scopes: string;
    // let state: GoogleLoginStates;
    // if (type === 'email') {
    //   scopes = `openid email profile https://www.googleapis.com/auth/gmail.readonly`;
    //   state = GoogleLoginStates.email;
    // } else {
    //   scopes = `openid email profile https://www.googleapis.com/auth/drive.file`;
    //   state = GoogleLoginStates.drive;
    // }
    // this.gmailLoginHttp(scopes.toString(), state);
    if (Capacitor.getPlatform() !== 'ios') {
      try {
        await GoogleAuth.signOut();
      } catch (error) {}
      await new Promise(res => setTimeout(res, 300)); 
      try {
        const response = await GoogleAuth.signIn();
        if (response) {
          const googleUser: User = {} as User;
          googleUser.email = response.email;
          googleUser.familyName = response.familyName;
          googleUser.givenName = response.givenName;
          googleUser.id = response.id;
          googleUser.imageUrl = response.imageUrl;
          googleUser.name = response.name;
          ((googleUser.loginFor = type === 'email' ? LoginFor.email : LoginFor.gDrive),
            (googleUser.authentication = {
              accessToken: response.authentication.accessToken,
              idToken: response.authentication.idToken,
              refreshToken: response.authentication.refreshToken,
            }));
          this.googleBrowserLoginHandler(googleUser, type);
        }
      } catch (error) {
        this.accountsSvc.redirectedAccount.next(true);
        this.toastSvc.presentToast(error?.message);
      } finally {
        this.isGoogleSignIn = false;
      }
    } else {
      try {
        const popupWidth = 500;
        const popupHeight = 500;
        const left = (window.screen.width - popupWidth) / 2;
        const top = (window.screen.height - popupHeight) / 2;
        const authOptions = {
          ...this.OAuth2AuthenticateOptions,
          web: {
            ...this.OAuth2AuthenticateOptions.web,
            windowOptions: `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`,
          },
        };
        const response = await GenericOAuth2.authenticate(authOptions);
        if (response) {
          const googleUser: User = {} as User;
          googleUser.email = response.email;
          googleUser.familyName = response?.family_name;
          googleUser.givenName = response.given_name;
          googleUser.id = response.id;
          googleUser.imageUrl = response.picture;
          googleUser.name = response.name;
          ((googleUser.loginFor = type === 'email' ? LoginFor.email : LoginFor.gDrive),
            (googleUser.authentication = {
              accessToken:
                response[
                  Capacitor.isNativePlatform() ? 'access_token_response' : 'authorization_response'
                ].access_token,
              idToken:
                response[
                  Capacitor.isNativePlatform() ? 'access_token_response' : 'authorization_response'
                ].id_token,
              refreshToken:
                response[
                  Capacitor.isNativePlatform() ? 'access_token_response' : 'authorization_response'
                ].refresh_token,
            }));
          this.googleBrowserLoginHandler(googleUser, type);
        }
      } catch (error) {
        this.accountsSvc.redirectedAccount.next(true);
        console.error('OAuth rejected', error);
      } finally {
        this.isGoogleSignIn = false;
      }
    }
  }

  async revokeToken(accessToken: string) {
    try {
      const resp = await fetch(`https://accounts.google.com/o/oauth2/revoke?token=${accessToken}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await resp.json();
    } catch (error) {
      console.log(error);
    }
    return;
  }

  async googleBrowserLoginHandler(googleUser: User, type) {
    try {
      if (type === LoginFor.email) {
        await this.accounts.setEmailAccount(
          googleUser.email,
          'google',
          googleUser,
          this.storageSvc.currentTimestamp() + googleUser.authentication.expiresIn
        );
        this.loadingSvc.dismiss();
        this.navCtrl.navigateForward('app/add-files/email-accounts');
      } else {
        await this.storageSvc.setJson(LoginFor.gDrive + '-' + googleUser.id, {
          user: googleUser,
          expires: this.storageSvc.currentTimestamp() + googleUser.authentication.expiresIn,
        });
        this.accountsSvc.redirectedAccount.next(true);
        this.loadingSvc.dismiss();
        await this.checkToken(googleUser.authentication.accessToken);
        // this.drivePickerService.createPicker(googleUser.authentication.accessToken);
        // let state = {
        //   userId: googleUser.id,
        //   accessToken: googleUser.authentication.accessToken,
        // };
        // await this.navCtrl.navigateRoot("app/add-files");
        // this.navCtrl.navigateForward("app/add-files/list", {
        //   state,
        // });
      }
    } catch (error) {
      this.toastSvc.presentToast('SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN');
    }
  }

  // async googleBrowserLoginHandler(data) {
  //   try {
  //     let mainSplit;
  //     let googleUserLoginFor: LoginFor;
  //     if (data.url.indexOf(GoogleLoginStates.email) > -1) {
  //       mainSplit = data.url.split(GoogleLoginStates.email);
  //       googleUserLoginFor = LoginFor.email;
  //     } else {
  //       mainSplit = data.url.split(GoogleLoginStates.drive);
  //       googleUserLoginFor = LoginFor.gDrive;
  //     }
  //     let googleUser: User = await this.getTokeyByCode(
  //       mainSplit,
  //       googleUserLoginFor
  //     );

  //     if (data.url.indexOf(GoogleLoginStates.email) > -1) {
  //       await this.accounts.setEmailAccount(
  //         googleUser.email,
  //         "google",
  //         googleUser,
  //         this.storageSvc.currentTimestamp() +
  //           googleUser.authentication.expiresIn
  //       );
  //       this.loadingSvc.dismiss();
  //       this.navCtrl.navigateForward("app/add-files/email-accounts");
  //     } else {
  //       await this.storageSvc.setJson(LoginFor.gDrive + "-" + googleUser.id, {
  //         user: googleUser,
  //         expires:
  //           this.storageSvc.currentTimestamp() +
  //           googleUser.authentication.expiresIn,
  //       });
  //       this.loadingSvc.dismiss();
  //       let state = {
  //         userId: googleUser.id,
  //         accessToken: googleUser.authentication.accessToken,
  //       };
  //       await this.navCtrl.navigateRoot("app/add-files");
  //       this.navCtrl.navigateForward("app/add-files/list", {
  //         state,
  //       });
  //     }
  //   } catch (error) {
  //     this.toastSvc.presentToast(
  //       this.translateService.instant("SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN")
  //     );
  //   }
  // }

  // getTokeyByCode(mainSplit, loginFor: LoginFor) {
  //   return new Promise<User>(async (resolve, reject) => {
  //     try {
  //       const secondPart = mainSplit[1].split("&");
  //       const code = secondPart[1].split("=")[1];
  //       const options = {
  //         url: `https://oauth2.googleapis.com/token?code=${code}&client_id=${
  //           environment.GAPI_CLIENT_ID
  //         }&client_secret=${environment.GAPI_SECRET}&redirect_uri=${
  //           environment.redirectBaseUrl + "google"
  //         }&grant_type=authorization_code`,
  //       };
  //       let authResp = await Http.post(options);

  //       const userOptions = {
  //         url: `https://www.googleapis.com/oauth2/v3/userinfo`,
  //         headers: { Authorization: "Bearer " + authResp.data.access_token },
  //       };
  //       let userInfo = await Http.get(userOptions);

  //       const user = {} as User;
  //       user.email = userInfo.data["email"];
  //       user.familyName = userInfo.data["family_name"];
  //       user.givenName = userInfo.data["given_name"];
  //       user.id = userInfo.data["email"];
  //       user.imageUrl = userInfo.data["picture"];
  //       user.name = userInfo.data["name"];
  //       user.loginFor = loginFor;

  //       user.authentication = {
  //         accessToken: authResp.data["access_token"],
  //         idToken: authResp.data["id_token"],
  //         refreshToken: authResp.data["refresh_token"],
  //         expiresIn: authResp.data["expires_in"],
  //       };
  //       resolve(user);
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // }

  async refreshAccessToken(googleUser: User) {
    return new Promise<User>(async (resolve, reject) => {
      try {
        let response: any = {};
        if (Capacitor.getPlatform() !== 'ios') {
          response = await GoogleAuth.refresh();
        } else {
          const resp = await GenericOAuth2.refreshToken({
            appId: environment.GAPI_CLIENT_ID_IOS,
            refreshToken: googleUser.authentication.refreshToken,
            accessTokenEndpoint: "https://oauth2.googleapis.com/token",
          });
          response.accessToken = resp.access_token;
          response.idToken = resp.id_token;
          response.refreshToken = resp.refresh_token;
          response.expiresIn = resp.expires_in;
        }
        if (response && googleUser?.authentication) {
          googleUser.authentication.accessToken = response.accessToken;
          googleUser.authentication.idToken = response.idToken;
          googleUser.authentication.refreshToken = response.refreshToken;
          if (googleUser.loginFor === LoginFor.email) {
            await this.accounts.setEmailAccount(
              googleUser.email,
              'google',
              googleUser,
              this.storageSvc.currentTimestamp() + googleUser.authentication.expiresIn
            );
          } else {
            await this.storageSvc.setJson(LoginFor.gDrive + '-' + googleUser.id, {
              user: googleUser,
              expires: this.storageSvc.currentTimestamp() + googleUser.authentication.expiresIn,
            });
            await this.checkToken(googleUser.authentication.accessToken);
            // this.drivePickerService.createPicker(googleUser.authentication.accessToken);
          }
          resolve(googleUser);
        } else {
          reject();
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // refreshAccessToken(googleUser: User) {
  //   return new Promise<User>(async (resolve, reject) => {
  //     try {
  //       const options = {
  //         url: `https://oauth2.googleapis.com/token?client_id=${environment.GAPI_CLIENT_ID}&client_secret=${environment.GAPI_SECRET}&refresh_token=${googleUser.authentication.refreshToken}&grant_type=refresh_token`,
  //       };
  //       let refreshUser = await Http.post(options);

  //       googleUser.authentication.accessToken =
  //         refreshUser.data["access_token"];
  //       googleUser.authentication.expiresIn = refreshUser.data["expires_in"];
  //       googleUser.authentication.idToken = refreshUser.data["id_token"];

  //       if (googleUser.loginFor === LoginFor.email) {
  //         await this.accounts.setEmailAccount(
  //           googleUser.email,
  //           "google",
  //           googleUser,
  //           this.storageSvc.currentTimestamp() +
  //             googleUser.authentication.expiresIn
  //         );
  //       } else {
  //         await this.storageSvc.setJson(
  //           GoogleLoginStates.drive + "-" + googleUser.id,
  //           {
  //             user: googleUser,
  //             expires:
  //               this.storageSvc.currentTimestamp() +
  //               googleUser.authentication.expiresIn,
  //           }
  //         );
  //       }

  //       resolve(googleUser);
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // }

  // async signOut() {
  //   await GoogleAuth.signOut();
  // }

  async googleSignIn(gAuth: any) {
    try {
      const expires = this.storageSvc.currentTimestamp() + this.ttl;
      await gAuth.signIn();
      const googleUser = gAuth.currentUser.get();
      const user = this.getUserFrom(googleUser);

      await this.accounts.setEmailAccount(user.email, 'google', user, expires);
      // this.storageSvc.setJson('email-' + user.id, { user, expires });
      return user;
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  loadMessages(
    userId: string = 'me',
    pageToken: string = '0',
    perPage: number = 100,
    searchText: string = '',
    accessToken: string
  ): Promise<GmailMessageResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const options = {
          format: 'full',
          // maxResults: perPage, // for now using default 100
          labelIds: ['INBOX'],
          pageToken: pageToken,
          q: searchText,
        };

        const res = await this.getGmailMessagesList(accessToken, options, userId);
        if (res.data.resultSizeEstimate == 0) {
          res.data.id = '0';
        } else {
          res.data.messages = await Promise.all(
            res.data.messages?.map(async (message) => {
              const options = { format: 'full' };
              const msg = await this.getMessage(accessToken, options, userId, message.id);
              const messageInfo = msg?.data || null;

              if (messageInfo) {
                return {
                  id: messageInfo.id || '',
                  subject: messageInfo.snippet || '',
                  message: messageInfo || '',
                };
              }
              return {};
            })
          );
        }
        if (res.data.messages?.length > 0) {
          res.data.messages = res.data.messages.filter((d) => !!d);
        }

        resolve(res.data);
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  getGmailMessagesList(accessToken: string, listOptions: any, userId) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const url = `https://gmail.googleapis.com/gmail/v1/users/${userId}/messages`;
        const options = {
          url,
          headers: { Authorization: 'Bearer ' + accessToken },
          params: JSON.parse(JSON.stringify(listOptions)),
        };
        const response: HttpResponse = await Http.get(options);
        if (response?.data?.error?.message) reject(response.data.error.message);
        else {
          resolve(response);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  getMessage(accessToken: string, listOptions: any, userId, messageId) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const url = `https://gmail.googleapis.com/gmail/v1/users/${userId}/messages/${messageId}`;
        const options = {
          url,
          headers: { Authorization: 'Bearer ' + accessToken },
          params: JSON.parse(JSON.stringify(listOptions)),
        };

        const response: HttpResponse = await Http.get(options);
        resolve(response);
      } catch (error) {
        resolve(null);
      }
    });
  }

  downloadAttachment(userId, messageId, attachmentId, accessToken) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const url = `https://gmail.googleapis.com/gmail/v1/users/${userId}/messages/${messageId}/attachments/${attachmentId}`;
        const options = {
          url,
          headers: { Authorization: 'Bearer ' + accessToken },
          params: {},
        };
        const response: HttpResponse = await Http.get(options);
        resolve(response?.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  getTotalResults(): number {
    return this.gmailTotalResults;
  }

  // async validateDriveToken(user: User, type: string): Promise<string> {
  //   const obj = await this.storageSvc.getJson(type + "-" + user.id);
  //   if (!obj) {
  //     return "";
  //   }
  //   const expires = obj.expires;
  //   return this.validateGoogleToken(user, expires);
  // }

  // async validateGoogleToken(user: User, expires): Promise<string> {
  //   if (
  //     expires < this.storageSvc.currentTimestamp() ||
  //     user.authentication === undefined
  //   ) {
  //     const res = await this.refreshToken("email", user);
  //     user.authentication = res;
  //   }
  //   return user.authentication.accessToken;
  // }

  private getUserFrom(googleUser: any) {
    const user = {} as User;
    const profile = googleUser.getBasicProfile();

    user.email = profile.getEmail();
    user.familyName = profile.getFamilyName();
    user.givenName = profile.getGivenName();
    user.id = profile.getId();
    user.imageUrl = profile.getImageUrl();
    user.name = profile.getName();

    const authResponse = googleUser.getAuthResponse(true);
    user.authentication = {
      accessToken: authResponse.access_token,
      idToken: authResponse.id_token,
      refreshToken: '',
    };

    return user;
  }

  // gmailLoginHttp(scopes: string, state: GoogleLoginStates) {
  //   return new Promise(async (resolve, reject) => {
  //     const url = `https://accounts.google.com/o/oauth2/v2/auth?scope=${scopes}&include_granted_scopes=true&response_type=code&state=${state}&redirect_uri=${
  //       environment.redirectBaseUrl + 'google'
  //     }&client_id=${environment.GAPI_CLIENT_ID}&access_type=offline&prompt=consent`;
  //     const browserType = Capacitor.isNativePlatform() ? '_system' : '_self';
  //     this.iab.create(url, browserType, 'location=no');
  //     resolve(true);
  //   });
  // }

  async checkToken(accessToken: string) {
    return new Promise(async (resolve) => {
      try {
        const resp = await fetch(
          `https://www.googleapis.com/oauth2/v3/tokeninfo?access_token=${accessToken}`
        );
        const response = await resp.json();
        if (response?.scope?.includes('https://www.googleapis.com/auth/drive.file')) {
          this.drivePickerService.createPicker(accessToken);
          resolve(true);
        } else {
          const header = this.translateService.instant('GOOGLE_DRIVE_ACCESS_REQUIRED');
          const message = this.translateService.instant('GOOGLE_DRIVE_ACCESS_REQUIRED_MSG');
          const yes = this.translateService.instant('RE_LOGIN');
          const no = this.translateService.instant('CANCEL');
          const alertResp = await this.alertService.customAlert(header, message, '', [
            {
              text: no,
              role: 'no',
              handler: async () => {
                await alertResp.dismiss();
                await this.revokeToken(accessToken);
                const accountExists = await this.accountsSvc.getGoogleDriveAccount();
                if (!!accountExists) {
                  this.accountsSvc.accountStateChanged.next([
                    ADD_FILE_OPTION_NAME.Google_Drive,
                    false,
                  ]);
                }
                resolve(false);
              },
            },
            {
              text: yes,
              role: 'yes',
              handler: async () => {
                await alertResp.dismiss();
                await this.revokeToken(accessToken);
                const accountExists = await this.accountsSvc.getGoogleDriveAccount();
                if (!!accountExists) {
                  this.accountsSvc.accountStateChanged.next([
                    ADD_FILE_OPTION_NAME.Google_Drive,
                    false,
                  ]);
                }
                await this.signIn(LoginFor.gDrive);
                resolve(true);
              },
            },
          ]);
        }
      } catch (error) {
        console.log("🚀 ~ GoogleService ~ checkToken ~ error:", error);
      }
    });
  }
}
